import { type ReactNode } from 'react';
import { useStore } from '@nanostores/react';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { useMedia } from 'react-use';
import { isHeroButtonInView, CtaButton } from '@hungryroot/homepage';
import { useGetFormattedPromo, useGetPromoData } from '@hungryroot/rest-hooks';
import {
  PRE_AUTH_CATALOG_GROCERIES_ROUTE,
  PRE_AUTH_CATALOG_MEALS_ROUTE,
  PRE_AUTH_CATALOG_RECIPES_ROUTE,
} from '@hungryroot/routes';
import { BreakpointQueryLarge } from '@hungryroot/tokens';
import { CatalogDesktopNavigationLinks } from '../CatalogDesktopNavigationLinks/CatalogDesktopNavigationLinks';
import { CatalogNavigation } from '../CatalogNavigation/CatalogNavigation';
import { MobileMenuCards } from '../MobileMenuCards/MobileMenuCards';
import { PreAuthHeader } from '../PreAuthHeader/PreAuthHeader';
import styles from './PromoMessage.module.css';

export function PromoMessage({
  action,
  className,
}: {
  action: ReactNode;
  className?: string;
}) {
  const { data: promoData } = useGetPromoData();
  const promoText = useGetFormattedPromo(styles.bold, promoData, styles.promo);

  const isDesktop = useMedia(BreakpointQueryLarge, false);
  const $isHeroButtonInView = useStore(isHeroButtonInView);

  return (
    <>
      <AnimatePresence>
        {!$isHeroButtonInView && (
          <motion.div
            initial={{ opacity: 0, top: '-100px' }}
            animate={{ opacity: 1, top: 0 }}
            exit={{ opacity: 0, top: '-100px' }}
            transition={{ duration: 0.45 }}
            className={clsx(styles.fixed, className)}
          >
            <PreAuthHeader
              actionContainerClassName={styles['nav-links-promo']}
              action={action}
              catalogNavigation={
                <CatalogNavigation
                  desktopNavLinks={
                    <CatalogDesktopNavigationLinks
                      navLinks={[
                        {
                          text: 'Recipes',
                          href: PRE_AUTH_CATALOG_RECIPES_ROUTE,
                        },
                        {
                          text: 'Groceries',
                          href: PRE_AUTH_CATALOG_GROCERIES_ROUTE,
                        },
                        {
                          text: 'Meals',
                          href: PRE_AUTH_CATALOG_MEALS_ROUTE,
                        },
                      ]}
                    />
                  }
                  mobileNavLinks={
                    <MobileMenuCards
                      navLinks={[
                        {
                          text: 'Recipes',
                          href: PRE_AUTH_CATALOG_RECIPES_ROUTE,
                          image: {
                            src: '/public/img/catalog/recipes_2x.png',
                            alt: 'Recipes in the Hungryroot catalog',
                          },
                        },
                        {
                          text: 'Groceries',
                          href: PRE_AUTH_CATALOG_GROCERIES_ROUTE,
                          image: {
                            src: '/public/img/catalog/groceries_2x.png',
                            alt: 'Grocery items in the Hungryroot catalog',
                          },
                        },
                        {
                          text: 'Meals',
                          href: PRE_AUTH_CATALOG_MEALS_ROUTE,
                          image: {
                            src: '/public/img/catalog/meals_2x.png',
                            alt: 'Heat and eat meals in the Hungryroot catalog',
                          },
                        },
                      ]}
                    />
                  }
                />
              }
            />
          </motion.div>
        )}
      </AnimatePresence>
      {promoText && (
        <CtaButton
          className={styles.button}
          isVisible={!isDesktop && !$isHeroButtonInView}
          text={promoText}
        />
      )}
    </>
  );
}
